import { DEFAULT_POLL_ATTEMPTS, DEFAULT_POLL_INTERVAL } from 'config/constants';

type PollStoppedReason = 'error' | 'exhausted' | 'resolved';

type PollingResult<T> = {
  attempts: number;
  result: null | T;
  stoppedReason: PollStoppedReason;
};

type PollOptions<T> = {
  interval?: number;
  maxAttempts?: number;
  onComplete?: (pollingResult: PollingResult<T>) => void;
  onError?: (error: Error) => void;
  shouldStop?: (result?: T) => boolean;
};

export async function executePolling<T>(
  fn: () => Promise<T>,
  options?: PollOptions<T>
): Promise<PollingResult<T>> {
  const {
    interval = DEFAULT_POLL_INTERVAL,
    maxAttempts = DEFAULT_POLL_ATTEMPTS,
    onComplete,
    onError,
    shouldStop,
  } = options ?? {};

  let attempts = 0;

  return new Promise((resolve) => {
    const finish = (pollingResult: PollingResult<T>) => {
      resolve(pollingResult);
      onComplete?.(pollingResult);
    };

    const attempt = async () => {
      attempts += 1;
      try {
        const result = await fn();
        if (shouldStop?.(result)) {
          finish({ attempts, result, stoppedReason: 'resolved' });
          return;
        }

        if (attempts >= maxAttempts) {
          finish({ attempts, result, stoppedReason: 'exhausted' });
          return;
        }

        setTimeout(attempt, interval);
      } catch (err) {
        onError?.(err as Error);
        finish({ attempts, result: null, stoppedReason: 'error' });
      }
    };

    attempt();
  });
}
